import { useReducer } from 'react'
import { BuildingProps } from './pfTable'

type ReducerState = {
  siteName: string
  floorAreaDisplay: string
  energy: string
  carbon: string
  renewables: string
  sortType: string
  sortKey: string
}

type ReducerAction = {
  type: string
  payload: string
}

const initialState: ReducerState = {
  siteName: 'none',
  floorAreaDisplay: 'none',
  energy: 'none',
  carbon: 'none',
  renewables: 'none',
  sortType: 'none',
  sortKey: 'none',
}

const reducer = (_state: ReducerState, action: ReducerAction): ReducerState => {
  const { type, payload } = action
  switch (type) {
    case 'asc':
      return {
        siteName: payload === 'siteName' ? 'asc' : 'none',
        floorAreaDisplay: payload === 'floorArea' ? 'asc' : 'none',
        energy: payload === 'energy' ? 'asc' : 'none',
        carbon: payload === 'carbon' ? 'asc' : 'none',
        renewables: payload === 'renewables' ? 'asc' : 'none',
        sortType: 'asc',
        sortKey: payload,
      }
    case 'dsc':
      return {
        siteName: payload === 'siteName' ? 'dsc' : 'none',
        floorAreaDisplay: payload === 'floorArea' ? 'dsc' : 'none',
        energy: payload === 'energy' ? 'dsc' : 'none',
        carbon: payload === 'carbon' ? 'dsc' : 'none',
        renewables: payload === 'renewables' ? 'dsc' : 'none',
        sortType: 'dsc',
        sortKey: payload,
      }
    case 'none':
    default:
      return {
        siteName: 'none',
        floorAreaDisplay: 'none',
        energy: 'none',
        carbon: 'none',
        renewables: 'none',
        sortType: 'none',
        sortKey: 'none',
      }
  }
}

const useMultiSort = () => {
  const [state, dispatch] = useReducer(reducer, initialState),
    { sortKey, sortType } = state

  const sortFunction = (a: BuildingProps, b: BuildingProps): number => {
    const isPortfolioContainsZero = (element: {
      value: number
      target: number
    }): boolean => !!(element.target || element.value)
    const portfolioContainsZeroA = a.tlData.some(isPortfolioContainsZero)
      ? 0
      : 1
    const portfolioContainsZeroB = b.tlData.some(isPortfolioContainsZero)
      ? 0
      : 1
    if (sortType === 'none') {
      return portfolioContainsZeroA - portfolioContainsZeroB === 0
        ? a.name.localeCompare(b.name)
        : portfolioContainsZeroA - portfolioContainsZeroB
    }

    if (sortKey === 'siteName') {
      return sortType === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    }

    if (sortKey === 'floorArea') {
      const valueA = typeof a.floorArea === 'number' ? a.floorArea : 0
      const valueB = typeof b.floorArea === 'number' ? b.floorArea : 0
      return sortType === 'asc' ? valueA - valueB : valueB - valueA
    }

    const valueA = a.tlData.find((datum) => datum.type === sortKey)?.value ?? 0
    const valueB = b.tlData.find((datum) => datum.type === sortKey)?.value ?? 0

    return sortType === 'asc' ? valueA - valueB : valueB - valueA
  }

  return {
    state,
    dispatch,
    sortFunction,
  }
}

export default useMultiSort
